import { IProjectInputDto, IProjectOutputDto, TemporalState } from 'src/app/core/models/api/sane-project/arpa-model';
import { parseNumber, toBooleanStrict, toValidId } from 'src/app/shared/utils';
import { ProjectViewModel } from '../models/project.viewmodel';
import { CalculatorProductViewModel } from '../sane-project-calculator/models/calculator-product.viewmodel';

type FlatDto = Record<string, string | number | boolean | undefined>;

export abstract class ProjectMapper {
  private static readonly outputMap: Record<string, Record<string, string>> = {
    allianzLifeOutput: {
      capitalDeath: 'vAllianzCapitalDeath',
      capitalIptp: 'vAllianzCapitalIptp',
      capitalIap: 'vAllianzCapitalIap'
    },
    axaLifeOutput: {
      capitalDeath: 'vAxaCapitalDeath',
      capitalIptp: 'vAxaCapitalIptp',
      capitalIap: 'vAxaCapitalIap',
      capitalSevereIllness: 'vAxaCapitalSevereIllness'
    },
    staLuciaLifeOutput: {
      capitalDeath: 'vStaLuciaCapitalDeath',
      capitalIptp: 'vStaLuciaCapitalIptp',
      capitalIap: 'vStaLuciaCapitalIap'
    },
    surneLifeOutput: {
      capitalDeath: 'vSurneCapitalDeath',
      capitalIptp: 'vSurneCapitalIptp',
      capitalIap: 'vSurneCapitalIap'
    },
    surneLifeProOutput: {
      capitalDeath: 'vSurneLifeProCapitalDeath',
      capitalIptp: 'vSurneLifeProCapitalIptp',
      capitalIap: 'vSurneLifeProCapitalIap'
    },
    helvetiaLifeOutput: {
      capitalDeath: 'vHelvetiaCapitalDeath',
      capitalIptp: 'vHelvetiaCapitalIptp',
      capitalIap: 'vHelvetiaCapitalIap'
    },
    prebalLifeOutput: {
      capitalDeath: 'vPrebalCapitalDeath',
      capitalIptp: 'vPrebalCapitalIptp',
      capitalIap: 'vPrebalCapitalIap'
    },
    prebalLifeDisabilityOutput: {
      capitalIptp: 'vPrebalInvProCapitalIptp',
      capitalIap: 'vPrebalInvProCapitalIap'
    },
    zurichLifeOutput: {
      capitalDeath: 'vZurichCapitalDeath',
      capitalIptp: 'vZurichCapitalIptp',
      capitalIap: 'vZurichCapitalIap'
    },
    axaAccidentOutput: {
      capitalDeath: 'aAxaCapitalDeath',
      capitalDeathTrafficAccident: 'aAxaCapitalDeathTrafficAccident',
      capitalHeartAttack: 'aAxaCapitalHeartAttack',
      capitalDisabilityType: 'aAxaCapitalDisabilityType',
      capitalDisability: 'aAxaCapitalDisability',
      capitalPermanentDisabilityTrafficAccident: 'aAxaCapitalPermanentDisabilityTrafficAccident',
      capitalMedicalExpensesForAccidents: 'aAxaCapitalMedicalExpensesForAccidents',
      capitalHospitalizationIllnessAccident: 'aAxaCapitalHospitalizationIllnessAccident',
      capitalFamilyAccidentAssistance: 'aAxaCapitalFamilyAccidentAssistance',
      capitalHIVHepatitisInfection: 'aAxaCapitalHIVHepatitisInfection'
    },
    surneAccidentOutput: {
      capitalDeath: 'aSurneCapitalDeath',
      capitalHeartAttack: 'aSurneCapitalHeartAttack',
      capitalAbsoluteDisabilityType: 'aSurneCapitalAbsoluteDisabilityType',
      capitalAbsoluteDisability: 'aSurneCapitalAbsoluteDisability',
      capitalTotalPermanentDisability: 'aSurneCapitalTotalPermanentDisability',
      capitalHealthDisabilityType: 'aSurneCapitalHealthDisabilityType',
      rateIdHIVHepatitisInfection: 'aSurneRateIdHIVHepatitisInfection'
    },
    prebalAccidentOutput: {
      capitalDeath: 'aPrebalCapitalDeath',
      capitalDeathTrafficAccident: 'aPrebalCapitalDeathTrafficAccident',
      capitalDisabilityType: 'aPrebalCapitalDisabilityType',
      capitalDisability: 'aPrebalCapitalDisability',
      hasSelectedCapitalDisability: 'aPrebalHasSelectedCapitalDisability',
      capitalHealthCareAccidents: 'aPrebalCapitalHealthCareAccidents',
      rateIdHIVHepatitisInfection: 'aPrebalRateIdHIVHepatitisInfection'
    },
    auraDeathOutput: {
      chargeable: 'decAuraSelectChargeable',
      taxAdvice: 'decAuraTaxAdvice',
      burial: 'decAuraBurial'
    },
    axaDependencyOutput: {
      severeDependency: 'depAxaSevereDependency',
      greatDependency: 'depAxaGreatDependency'
    },
    surneDependencyOutput: {
      capitalIncome: 'depSurneCapitalIncome',
      severeDependency: 'depSurneSevereDependency',
      greatDependency: 'depSurneGreatDependency'
    },
    pmTIEliteOutput: {
      subsidy: 'itPmEliteSubsidy',
      franchise: 'itPmEliteFranchise',
      duration: 'itPmEliteDuration',
      hospitalization: 'itPmEliteHospitalization'
    },
    pmTICoegOutput: {
      subsidy: 'itPmCoegSubsidy',
      franchise: 'itPmCoegFranchise',
      duration: 'itPmCoegDuration'
    },
    surneTIOutput: {
      subsidy: 'itSurnePlusSubsidy',
      franchise: 'itSurnePlusFranchise',
      duration: 'itSurnePlusDuration',
      hospitalization: 'itSurnePlusHospitalization'
    },
    surneTIVipOutput: {
      subsidy: 'itSurneVipSubsidy',
      franchise: 'itSurneVipFranchise',
      duration: 'itSurnaVipDuration'
    },
    prebalTIOutput: {
      subsidy: 'itPrebalSubsidy',
      franchise: 'itPrebalFranchise',
      duration: 'itPrebalDuration',
      hospitalization: 'itPrebalHospitalization',
      hasLife: 'itPrebalHasLife'
    },
    staLuciaWholeLifeOutput: {
      capitalDeath: 'veStaLuciaCapitalDeath',
      capitalIptp: 'veStaLuciaCapitalIptp',
      capitalIap: 'veStaLuciaCapitalIap'
    }
  };

  private static flattenBackendDto(grouped: IProjectOutputDto): FlatDto {
    if (!grouped) { return {}; }
    return Object.entries(this.outputMap).reduce<FlatDto>((flat, [sectionKey, map]) => {
      const section = (grouped as any)[sectionKey] || {};
      Object.entries(map).forEach(([sourceKey, targetKey]) => {
        const val = (section as any)[sourceKey];
        if (val !== null && val !== undefined) {
          flat[sourceKey] = val;
          flat[targetKey] = val;
        }
      });
      return flat;
    }, {});
  }

  private static patchCategory<T extends Record<string, CalculatorProductViewModel>>(category: T, flat: FlatDto): T {
    Object.values(category).forEach(vm => {
      const patch = Object.keys(vm.form.controls).reduce((acc, ctrl) => {
        const value = flat[ctrl];
        if (value !== null && value !== undefined) { acc[ctrl] = value; }
        return acc;
      }, {} as Record<string, any>);
      vm.form.patchValue(patch, { emitEvent: false });
    });
    return category;
  }

  public static mapForProject(vm: ProjectViewModel): IProjectInputDto {
    const {
      edit,
      calculatorLife,
      calculatorLifeWhole,
      calculatorAccidents,
      calculatorIT,
      calculatorDependence,
      calculatorDeaths
    } = vm.form.getRawValue();

    const e = edit;
    const {
      allianz: lifeAll,
      axa: lifeAxa,
      staLucia: lifeSanta,
      surne: lifeSurne,
      surneLifePro: lifePro,
      helvetia: lifeHelvetia,
      prebal: lifePrebal,
      prebalInvPro: lifePrebalInvPro,
      zurich: lifeZurich
    } = calculatorLife;

    const lifeWhole = calculatorLifeWhole.staLucia;
    const { axa: accAxa, surne: accSurne, prebal: accPrebal } = calculatorAccidents;
    const { pmElite: itElite, pmCoeg: itCoeg, prebal: itPrebal, surnePlus: itPlus, surneVip: itVip } = calculatorIT;
    const { axa: depAxa, surne: depSurne } = calculatorDependence;
    const decAura = calculatorDeaths.aura;

    const mutual = toValidId(e.projectMutualIncome);
    const retaQuote = toValidId(e.projectContributionRETA);
    const generalRegimeQuote = toValidId(e.projectContributionGeneralReg);
    const regimeIds = [retaQuote, generalRegimeQuote, mutual].filter((v): v is number => v !== null);

    return {
      regime: (e.projectActivity as string).trim(),
      professionCode: parseNumber(e.projectClientProfessionCode),
      professionName: e.projectClientProfession,
      clientId: parseNumber(e.projectClientId),

      effectiveDate: e.projectEffectiveDate,
      birthDate: e.projectClientBirthDate,
      paymentType: parseNumber(e.projectPaymentType),
      projectName: e.projectName,
      clientName: e.projectClientName,

      clientSex: Number(e.projectClientSex ?? 0),

      sport: parseNumber(e.projectSport),
      risks: parseNumber(e.projectLifeEspecialRisks),
      motorcycle: parseNumber(e.projectLifeMotorbike),
      motor: parseNumber(e.projectLifeDrivingForce),
      scaffolding: parseNumber(e.projectLifeScaffoldRoof),
      lifeLevel: parseNumber(e.projectLivingStandard),
      married: toBooleanStrict(e.projectMarried),
      children: parseNumber(e.projectMinorChildren),
      saneIntranet: parseNumber(e.projectSaneIntranet),
      chargeable: true,

      officeAlias: (e.projectClientOfficeAlias ?? '').trim() || e.projectClientOfficeCode,
      officeName: (e.projectClientOfficeName ?? '').trim(),
      totalBonus: vm.totalBonus,

      life: e.projectIndustryLife ? 1 : 0,
      accidents: e.projectIndustryAccident ? 1 : 0,
      it: e.projectIndustryTemporaryIncapacity ? 1 : 0,
      dependency: e.projectIndustryDependency ? 1 : 0,
      wholeLife: e.projectIndustryWholeLife ? 1 : 0,
      deaths: e.projectIndustryDeath ? 1 : 0,

      mutual: mutual ?? 0,
      retaQuote: retaQuote ?? 0,
      generalRegimeQuote: generalRegimeQuote ?? 0,

      iptp: parseNumber(e.projectDeadlineIPT),
      iap: parseNumber(e.projectIap),

      lifeRegimeLevel: regimeIds.join(','),

      temporalState: vm.temporalState,

      allianzLifeInput: {
        capitalDeath: parseNumber(lifeAll.vAllianzCapitalDeath),
        capitalIptp: parseNumber(lifeAll.vAllianzCapitalIptp),
        capitalIap: parseNumber(lifeAll.vAllianzCapitalIap)
      },
      axaLifeInput: {
        capitalDeath: parseNumber(lifeAxa.vAxaCapitalDeath),
        capitalIptp: parseNumber(lifeAxa.vAxaCapitalIptp),
        capitalIap: parseNumber(lifeAxa.vAxaCapitalIap),
        capitalSevereIllness: parseNumber(lifeAxa.vAxaCapitalSevereIllness)
      },
      staLuciaLifeInput: {
        capitalDeath: parseNumber(lifeSanta.vStaLuciaCapitalDeath),
        capitalIptp: parseNumber(lifeSanta.vStaLuciaCapitalIptp),
        capitalIap: parseNumber(lifeSanta.vStaLuciaCapitalIap)
      },
      surneLifeInput: {
        capitalDeath: parseNumber(lifeSurne.vSurneCapitalDeath),
        capitalIptp: parseNumber(lifeSurne.vSurneCapitalIptp),
        capitalIap: parseNumber(lifeSurne.vSurneCapitalIap)
      },
      surneLifeProInput: {
        capitalDeath: parseNumber(lifePro.vSurneLifeProCapitalDeath),
        capitalIptp: parseNumber(lifePro.vSurneLifeProCapitalIptp),
        capitalIap: parseNumber(lifePro.vSurneLifeProCapitalIap)
      },
      helvetiaLifeInput: {
        capitalDeath: parseNumber(lifeHelvetia.vHelvetiaCapitalDeath),
        capitalIptp: parseNumber(lifeHelvetia.vHelvetiaCapitalIptp),
        capitalIap: parseNumber(lifeHelvetia.vHelvetiaCapitalIap)
      },
      prebalLifeInput: {
        capitalDeath: parseNumber(lifePrebal.vPrebalCapitalDeath),
        capitalIptp: parseNumber(lifePrebal.vPrebalCapitalIptp),
        capitalIap: parseNumber(lifePrebal.vPrebalCapitalIap)
      },
      prebalLifeDisabilityInput: {
        capitalIptp: parseNumber(lifePrebalInvPro.vPrebalInvProCapitalIptp),
        capitalIap: parseNumber(lifePrebalInvPro.vPrebalInvProCapitalIap)
      },
      zurichLifeInput: {
        capitalDeath: parseNumber(lifeZurich.vZurichCapitalDeath),
        capitalIptp: parseNumber(lifeZurich.vZurichCapitalIptp),
        capitalIap: parseNumber(lifeZurich.vZurichCapitalIap)
      },
      wholeLifeStaLuciaInput: {
        capitalDeath: parseNumber(lifeWhole.veStaLuciaCapitalDeath),
        capitalIptp: parseNumber(lifeWhole.veStaLuciaCapitalIptp),
        capitalIap: parseNumber(lifeWhole.veStaLuciaCapitalIap)
      },

      axaAccidentInput: {
        capitalDeath: parseNumber(accAxa.aAxaCapitalDeath),
        capitalDeathTrafficAccident: parseNumber(accAxa.aAxaCapitalDeathTrafficAccident),
        capitalHeartAttack: parseNumber(accAxa.aAxaCapitalHeartAttack),
        capitalDisabilityType: parseNumber(accAxa.aAxaCapitalDisabilityType),
        capitalDisability: parseNumber(accAxa.aAxaCapitalDisability),
        capitalPermanentDisabilityTrafficAccident: parseNumber(accAxa.aAxaCapitalPermanentDisabilityTrafficAccident),
        capitalMedicalExpensesForAccidents: parseNumber(accAxa.aAxaCapitalMedicalExpensesForAccidents),
        capitalHospitalizationIllnessAccident: parseNumber(accAxa.aAxaCapitalHospitalizationIllnessAccident),
        capitalFamilyAccidentAssistance: parseNumber(accAxa.aAxaCapitalFamilyAccidentAssistance),
        capitalHIVHepatitisInfection: parseNumber(accAxa.aAxaCapitalHIVHepatitisInfection)
      },
      surneAccidentInput: {
        capitalDeath: parseNumber(accSurne.aSurneCapitalDeath),
        capitalHeartAttack: parseNumber(accSurne.aSurneCapitalHeartAttack),
        capitalAbsoluteDisabilityType: parseNumber(accSurne.aSurneCapitalAbsoluteDisabilityType),
        capitalAbsoluteDisability: parseNumber(accSurne.aSurneCapitalAbsoluteDisability),
        capitalTotalPermanentDisability: parseNumber(accSurne.aSurneCapitalTotalPermanentDisability),
        capitalHealthDisabilityType: parseNumber(accSurne.aSurneCapitalHealthDisabilityType),
        rateIdHIVHepatitisInfection: parseNumber(accSurne.aSurneRateIdHIVHepatitisInfection)
      },
      prebalAccidentInput: {
        capitalDisabilityType: parseNumber(accPrebal.aPrebalCapitalDisabilityType),
        capitalDisability: parseNumber(accPrebal.aPrebalCapitalDisability),
        capitalDeathTrafficAccident: parseNumber(accPrebal.aPrebalCapitalDeathTrafficAccident),
        capitalDeath: parseNumber(accPrebal.aPrebalCapitalDeath),
        rateIdHIVHepatitisInfection: parseNumber(accPrebal.aPrebalRateIdHIVHepatitisInfection),
        capitalHealthCareAccidents: parseNumber((accPrebal as any).capitalHealthCareAccidents ?? accPrebal.aPrebalCapitalHealthCareAccidents),
        hasSelectedCapitalDisability: parseNumber(accPrebal.aPrebalHasSelectedCapitalDisability)
      },

      pmTIEliteInput: {
        duration: parseNumber(itElite.itPmEliteDuration),
        subsidy: parseNumber(itElite.itPmEliteSubsidy),
        franchise: parseNumber(itElite.itPmEliteFranchise),
        hospitalization: parseNumber(itElite.itPmEliteHospitalization)
      },
      pmTICoegInput: {
        subsidy: parseNumber(itCoeg.itPmCoegSubsidy),
        franchise: parseNumber(itCoeg.itPmCoegFranchise),
        duration: parseNumber(itCoeg.itPmCoegDuration)
      },
      surneTIInput: {
        subsidy: parseNumber(itPlus.itSurnePlusSubsidy),
        duration: parseNumber(itPlus.itSurnePlusDuration),
        hospitalization: parseNumber(itPlus.itSurnePlusHospitalization),
        franchise: parseNumber(itPlus.itSurnePlusFranchise)
      },
      surneTIVipInput: {
        subsidy: parseNumber(itVip.itSurneVipSubsidy),
        duration: parseNumber(itVip.itSurneVipDuration),
        hospitalization: parseNumber((itVip as any).itSurneVipHospitalization),
        franchise: parseNumber(itVip.itSurneVipFranchise)
      },
      prebalTIInput: {
        duration: parseNumber(itPrebal.itPrebalDuration),
        subsidy: parseNumber(itPrebal.itPrebalSubsidy),
        hospitalization: parseNumber(itPrebal.itPrebalHospitalization),
        hasLife: toBooleanStrict(itPrebal.itPrebalHasLife),
        franchise: parseNumber(itPrebal.itPrebalFranchise)
      },

      axaDependencyInput: {
        severeDependency: parseNumber(depAxa.depAxaSevereDependency),
        greatDependency: parseNumber(depAxa.depAxaGreatDependency)
      },
      surneDependencyInput: {
        capitalIncome: parseNumber(depSurne.depSurneCapitalIncome),
        severeDependency: parseNumber(depSurne.depSurneSevereDependency),
        greatDependency: parseNumber(depSurne.depSurneGreatDependency)
      },

      auraDeathInput: {
        chargeable: toBooleanStrict(decAura.decAuraSelectChargeable),
        taxAdvice: parseNumber(decAura.decAuraTaxAdvice),
        burial: parseNumber(decAura.decAuraBurial)
      }
    };
  }

  public static mapDtoToViewModel(vm: ProjectViewModel, dto: IProjectOutputDto): void {
    vm.edit.form.patchValue({ projectClientId: dto.clientId }, { emitEvent: false });
    vm.temporalState = dto.temporalState ?? TemporalState.Inactive;

    vm.edit.form.patchValue({
      projectId: dto.id,
      projectName: dto.projectName,
      projectEffectiveDate: dto.effectiveDate,
      projectPaymentType: String(dto.paymentType),
      projectActivity: dto.regime,
      projectSport: String(dto.sport),
      projectIndustryLife: dto.life === 1,
      projectIndustryAccident: dto.accidents === 1,
      projectIndustryTemporaryIncapacity: dto.it === 1,
      projectIndustryDependency: dto.dependency === 1,
      projectIndustryWholeLife: dto.wholeLife === 1,
      projectIndustryDeath: dto.deaths === 1,
      projectLivingStandard: String(dto.lifeLevel),
      projectDeadlineIPT: String(dto.iptp),

      projectContributionRETA: dto.retaQuote > 0 ? String(dto.retaQuote) : null,
      projectContributionGeneralReg: dto.generalRegimeQuote > 0 ? String(dto.generalRegimeQuote) : null,

      projectMarried: dto.married,
      projectMutualIncome: String(dto.mutual),
      projectMinorChildren: String(dto.children),
      projectLifeDrivingForce: dto.motor,
      projectLifeScaffoldRoof: dto.scaffolding,
      projectLifeEspecialRisks: dto.risks,
      projectLifeMotorbike: String(dto.motorcycle),
      projectClientName: dto.clientFullName,
      projectClientBirthDate: dto.birthDate,
      projectClientActuarialAge: dto.age,
      projectClientProfession: dto.professionName,
      projectClientProvince: dto.officeName,
      projectClientOfficeCode: dto.officeAlias,
      projectClientOfficeName: dto.officeName,
      projectClientProfessionCode: dto.professionCode,
      projectClientSex: dto.clientSex
    }, { emitEvent: false });

    const statuses = vm.edit.maritalStatuses;
    const marriedOpt = statuses.find(s => /casad/i.test(s.description));
    const singleOpt = statuses.find(s => /solter/i.test(s.description));
    vm.edit.form.patchValue(
      { projectMarried: dto.married ? marriedOpt?.id?.toString() ?? '' : singleOpt?.id?.toString() ?? '' },
      { emitEvent: false }
    );

    const flat = this.flattenBackendDto(dto);
    this.patchCategory(vm.calculatorLife, flat);
    this.patchCategory(vm.calculatorAccidents, flat);
    this.patchCategory(vm.calculatorIT, flat);
    this.patchCategory(vm.calculatorDependence, flat);
    this.patchCategory(vm.calculatorLifeWhole, flat);
    this.patchCategory(vm.calculatorDeaths, flat);

    const axaIn = dto.axaAccidentOutput;
    if (axaIn) {
      vm.calculatorAccidents.axa.form.patchValue({
        aAxaCapitalDeath: axaIn.capitalDeath,
        aAxaCapitalDeathTrafficAccident: axaIn.capitalDeathTrafficAccident,
        aAxaCapitalHeartAttack: axaIn.capitalHeartAttack,
        aAxaCapitalDisabilityType: axaIn.capitalDisabilityType,
        aAxaCapitalDisability: axaIn.capitalDisability,
        aAxaCapitalPermanentDisabilityTrafficAccident: axaIn.capitalPermanentDisabilityTrafficAccident,
        aAxaCapitalMedicalExpensesForAccidents: axaIn.capitalMedicalExpensesForAccidents,
        aAxaCapitalHospitalizationIllnessAccident: axaIn.capitalHospitalizationIllnessAccident,
        aAxaCapitalFamilyAccidentAssistance: axaIn.capitalFamilyAccidentAssistance,
        aAxaCapitalHIVHepatitisInfection: axaIn.capitalHIVHepatitisInfection
      }, { emitEvent: false });
    }

    const surneIn = dto.surneAccidentOutput;
    if (surneIn) {
      vm.calculatorAccidents.surne.form.patchValue({
        aSurneCapitalDeath: surneIn.capitalDeath,
        aSurneCapitalHeartAttack: surneIn.capitalHeartAttack,
        aSurneCapitalAbsoluteDisabilityType: surneIn.capitalAbsoluteDisabilityType,
        aSurneCapitalAbsoluteDisability: surneIn.capitalAbsoluteDisability,
        aSurneCapitalTotalPermanentDisability: surneIn.capitalTotalPermanentDisability,
        aSurneCapitalHealthDisabilityType: surneIn.capitalHealthDisabilityType,
        aSurneRateIdHIVHepatitisInfection: surneIn.rateIdHIVHepatitisInfection
      }, { emitEvent: false });
    }

    const prebalIn = dto.prebalAccidentOutput;
    if (prebalIn) {
      vm.calculatorAccidents.prebal.form.patchValue({
        aPrebalCapitalDeath: prebalIn.capitalDeath,
        aPrebalCapitalDeathTrafficAccident: prebalIn.capitalDeathTrafficAccident,
        aPrebalCapitalDisabilityType: prebalIn.capitalDisabilityType,
        aPrebalCapitalDisability: prebalIn.capitalDisability,
        aPrebalHasSelectedCapitalDisability: prebalIn.hasSelectedCapitalDisability,
        capitalHealthCareAccidents: prebalIn.capitalHealthCareAccidents,
        aPrebalRateIdHIVHepatitisInfection: prebalIn.rateIdHIVHepatitisInfection
      }, { emitEvent: false });
    }

    const wlIn = dto.staLuciaWholeLifeOutput;
    if (wlIn) {
      vm.calculatorLifeWhole.staLucia.form.patchValue({
        veStaLuciaCapitalDeath: wlIn.capitalDeath,
        veStaLuciaCapitalIptp: wlIn.capitalIptp,
        veStaLuciaCapitalIap: wlIn.capitalIap
      }, { emitEvent: false });
    }
  }
}
